import { motion } from 'framer-motion';

interface CardSkeletonProps {
  count?: number;
  type?: 'school' | 'faq' | 'default' | 'rectangular';
}

const CardSkeleton = ({ count = 1, type = 'default' }: CardSkeletonProps) => {
  const skeletons = Array.from({ length: count });

  if (type === 'school') {
    return (
      <>
        {skeletons.map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="flex">
              {/* Image Skeleton */}
              <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-ivory-100 animate-pulse" />
              
              {/* Content Skeleton */}
              <div className="p-3 md:p-4 flex flex-col flex-1 min-w-0">
                {/* Name and Badges */}
                <div className="mb-2">
                  <div className="h-4 bg-ivory-200 rounded w-3/4 mb-2 animate-pulse" />
                  <div className="h-3 bg-ivory-200 rounded w-1/2 animate-pulse" />
                </div>
                
                {/* Location */}
                <div className="h-3 bg-ivory-200 rounded w-2/3 mb-2 animate-pulse" />
                
                {/* Rating */}
                <div className="h-3 bg-ivory-200 rounded w-1/3 mb-2 animate-pulse" />
                
                {/* Description */}
                <div className="space-y-1 mb-2">
                  <div className="h-2 bg-ivory-200 rounded w-full animate-pulse" />
                  <div className="h-2 bg-ivory-200 rounded w-5/6 animate-pulse" />
                </div>
                
                {/* Features */}
                <div className="space-y-1 mb-2">
                  <div className="h-2 bg-ivory-200 rounded w-2/3 animate-pulse" />
                  <div className="h-2 bg-ivory-200 rounded w-1/2 animate-pulse" />
                </div>
                
                {/* Price */}
                <div className="bg-ivory-50 rounded-md p-2 mb-2">
                  <div className="h-4 bg-ivory-200 rounded w-1/3 mb-1 animate-pulse" />
                  <div className="h-2 bg-ivory-200 rounded w-1/4 animate-pulse" />
                </div>
                
                {/* Buttons */}
                <div className="flex space-x-2 mt-auto">
                  <div className="flex-1 h-8 bg-ivory-200 rounded-md animate-pulse" />
                  <div className="flex-1 h-8 bg-ivory-200 rounded-md animate-pulse" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </>
    );
  }

  if (type === 'rectangular') {
    return (
      <>
        {skeletons.map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-midnight/5 overflow-hidden h-[280px]"
          >
            <div className="flex flex-col md:flex-row h-full">
              {/* Left Side - Image Skeleton */}
              <div className="w-full md:w-64 lg:w-80 h-full md:h-full flex-shrink-0 bg-fog animate-pulse" />
              
              {/* Right Side - Content Skeleton */}
              <div className="flex-1 p-6 flex flex-col">
                {/* Header Section */}
                <div className="mb-4">
                  {/* Title - matches text-5 font-serif font-bold */}
                  <div className="h-6 bg-midnight/10 rounded w-3/4 mb-2 animate-pulse" />
                  
                  {/* School Name - matches font-semibold text-2 */}
                  <div className="h-4 bg-midnight/10 rounded w-1/2 mb-1.5 animate-pulse" />
                  
                  {/* Location - matches flex items-center gap-1.5 text-2 */}
                  <div className="h-3.5 bg-midnight/10 rounded w-2/5 mb-3 animate-pulse" />
                  
                  {/* Badges - matches flex flex-wrap gap-2 */}
                  <div className="flex flex-wrap gap-2">
                    {/* Duration badge - matches px-2 py-1 bg-fog rounded-md */}
                    <div className="h-6 bg-fog rounded-md w-20 animate-pulse" />
                    {/* Difficulty badge - matches px-2 py-1 bg-orange/10 rounded-md */}
                    <div className="h-6 bg-orange/10 rounded-md w-16 animate-pulse" />
                    {/* Rating badge - matches px-2 py-1 bg-yellow-50 rounded-md */}
                    <div className="h-6 bg-yellow-50 rounded-md w-14 animate-pulse" />
                  </div>
                </div>
                
                {/* Spacer to push bottom section down */}
                <div className="flex-1" />
                
                {/* Bottom Section - Price and Action Button */}
                <div className="flex items-center justify-between pt-4 border-t border-midnight/5 mt-auto">
                  {/* Price Section */}
                  <div className="flex-1">
                    {/* Price - matches text-5 font-bold */}
                    <div className="h-6 bg-midnight/10 rounded w-32 mb-1 animate-pulse" />
                    {/* Subtitle - matches text-2 */}
                    <div className="h-4 bg-midnight/10 rounded w-24 animate-pulse" />
                  </div>
                  {/* Action Button - matches px-6 py-2.5 bg-orange rounded-lg */}
                  <div className="ml-4">
                    <div className="h-10 bg-orange/30 rounded-lg w-32 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </>
    );
  }

  if (type === 'faq') {
    return (
      <>
        {skeletons.map((_, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-midnight/5 overflow-hidden"
          >
            {/* Question Button Skeleton - matches actual structure */}
            <div className="w-full py-6 px-6 bg-orange">
              <div className="flex justify-between items-center">
                <div className="h-5 bg-white/30 rounded w-3/4 animate-pulse" />
                <div className="h-6 w-6 bg-white/30 rounded flex-shrink-0 animate-pulse" />
              </div>
            </div>
          </motion.div>
        ))}
      </>
    );
  }

  // Default skeleton
  return (
    <>
      {skeletons.map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md p-4 animate-pulse"
        >
          <div className="h-4 bg-ivory-200 rounded w-3/4 mb-3" />
          <div className="h-3 bg-ivory-200 rounded w-full mb-2" />
          <div className="h-3 bg-ivory-200 rounded w-5/6" />
        </div>
      ))}
    </>
  );
};

export default CardSkeleton;


